import { useEffect, useRef, useState } from 'react'

import { IconLogout } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogDismiss,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { TwoFactorSetup } from '@/components/common/two-factor-setup'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useContextStore } from '@/stores/context'

import {
  trackEnforce2FaContinueClicked,
  trackEnforce2FaLogoutClicked,
  trackEnforce2FaModalViewed,
  trackEnforce2FaSetupCompleted,
} from './enforce-2fa-dialog.tracking'
import locales from './locales'

type Enforce2FaStep = 'intro' | 'setup'

type Enforce2FaDialogProps = {
  onClose: () => void
}

export function Enforce2FaDialog({ onClose }: Enforce2FaDialogProps) {
  const { t } = useComponentTranslation(locales)
  const navigate = useNavigate()

  const [step, setStep] = useState<Enforce2FaStep>('intro')

  const currentPlan = useContextStore(
    (state) =>
      state.userAccountContext?.balanceInformation.featureType ?? 'Free',
  )

  const hasTrackedViewRef = useRef(false)
  useEffect(() => {
    if (hasTrackedViewRef.current) return
    hasTrackedViewRef.current = true
    trackEnforce2FaModalViewed({ currentPlan })
  }, [currentPlan])

  const handleLogout = () => {
    trackEnforce2FaLogoutClicked({ currentPlan })
    navigate({ to: '/logout' })
  }

  const handleContinue = () => {
    trackEnforce2FaContinueClicked({ currentPlan })
    setStep('setup')
  }

  // Tracked at activation (not at the recovery-codes Done click) so the
  // "configured" event fires even if the user abandons the codes screen.
  const handleActivated = () => {
    trackEnforce2FaSetupCompleted({ currentPlan })
  }

  // Never dismissable — overlay click, Escape, and (on mobile) swipe-down are
  // all disabled: the account enforces 2FA, so the only ways out are
  // completing the setup or signing out.
  return (
    <ResponsiveDialog open onOpenChange={() => {}} dismissible={false}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{t('title')}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {step === 'intro' ? t('intro.description') : t('setup.description')}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        {step === 'intro' && (
          <>
            <ResponsiveDialogBody>
              <p className="text-sm">{t('intro.explanation')}</p>
            </ResponsiveDialogBody>
            <ResponsiveDialogFooter>
              <ResponsiveDialogDismiss intent="close" onClick={handleLogout}>
                <IconLogout className="h-4 w-4" />
                {t('intro.actions.logout')}
              </ResponsiveDialogDismiss>
              <Button onClick={handleContinue}>
                {t('intro.actions.continue')}
              </Button>
            </ResponsiveDialogFooter>
          </>
        )}
        {step === 'setup' && (
          <TwoFactorSetup
            onActivated={handleActivated}
            onCompleted={onClose}
            onCancel={() => setStep('intro')}
          />
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
